import { createContext, ReactNode, useCallback, useContext, useState } from "react";
import { Toast, ToastVariant } from "../components/ui/Toast";

export interface ToastOptions {
  /**
   * Disattiva la scomparsa automatica: il toast resta finché non lo si chiude
   * con la X. Da usare quando il messaggio contiene un dato che l'operatore
   * deve trascrivere o usare (es. una password temporanea da comunicare a
   * mano): tre secondi non bastano per leggerlo, e una volta sparito non è
   * più recuperabile.
   */
  persistent?: boolean;
  /** Se valorizzato, mostra un pulsante "Copia" che copia SOLO questo testo. */
  copyValue?: string;
}

export interface ToastItem extends ToastOptions {
  id: string;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const AUTO_DISMISS_MS = 3000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismiss = useCallback((id: string) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, variant: ToastVariant = "info", options: ToastOptions = {}) => {
      const id = crypto.randomUUID();
      setToasts((current) => [...current, { id, message, variant, ...options }]);

      // Auto-dismiss dopo 3 secondi — il pulsante X resta comunque disponibile
      // per la chiusura manuale immediata (regola UX critica del documento).
      // Con `persistent` il timer non parte affatto: la chiusura è solo manuale.
      if (!options.persistent) {
        window.setTimeout(() => dismiss(id), AUTO_DISMISS_MS);
      }
    },
    [dismiss]
  );

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* z-toast: sopra pannelli e modali. A z-50 le conferme e gli errori
          finivano dietro a un SlideOver aperto (z-1100), cioè sparivano
          proprio mentre si salvava da un pannello. Vedi tailwind.config.ts. */}
      <div
        className="fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col gap-2"
        role="region"
        aria-label="Notifiche"
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            variant={toast.variant}
            copyValue={toast.copyValue}
            onClose={() => dismiss(toast.id)}
          >
            {toast.message}
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast deve essere usato all'interno di <ToastProvider>.");
  }
  return ctx;
}
